/**
 * Sense → Pressure. Slice 59, the pillar's first screen.
 *
 * ## WHAT THE PAGE IS FOR
 *
 * One question — *am I messaging the same people too often?* — answered three
 * ways, top to bottom: how the whole workspace splits across the three bands,
 * which segments hold the people at Warning or worse, and who those people are.
 * The last two are the action: a segment here is the one to rest or to send
 * less to, and the schedule step's "rest people at Critical" switch is where
 * that is done for a campaign.
 *
 * ## WHAT IT DELIBERATELY DOES NOT SAY
 *
 * No explanatory paragraph on the surface (slice 38's rule): what counts as a
 * message lives behind one `MoreDetail`. No response rate beside the bands —
 * slice 59 records why it waits for the funnel (slice 60): a response figure is
 * measured on some channels and not others, and on this page it would have to
 * say "not measured" for most workspaces most of the time, which is a sentence
 * the band beside it cannot afford to be confused with.
 *
 * ## ZERO IS TRUE HERE
 *
 * Every figure on this page is a count of messages Wend handed to a provider, so
 * a zero is a measured zero — the opposite of most of the product — and is drawn
 * as one. The one state that gets words instead of numbers is a workspace with
 * no people at all, because three zero tiles over nobody describe nothing.
 *
 * Slice 65 gave every Sense screen the same band at the top: the eyebrow
 * "Sense" with the pillar's glyph, and one line on what this screen answers.
 * Since the split into Reach, Connect and Sense, a person arriving here from a
 * link on the Overview or a campaign page needs to be told which part of Wend
 * they are in; the sidebar says it too, but only to someone who looks there.
 * The band is drawn by `PressurePage` and not by `PressureContent`, as on the
 * other four screens it sits in the Page wrapper and not in the part that
 * draws the data, so the data's own renders never carry it. The name and glyph
 * come from `lib/pillars.tsx`, the one place a pillar is named.
 *
 * ## THE BLUEPRINT'S PANEL, BARS AND PEOPLE — SLICE 66
 *
 * The bands are one `MetricPanel` now — the people in Audiences as its figure,
 * the messages sent as its sub-line — with the three bands as `BySourceBars`
 * rows under it: the band's name and range, a meter, the count, "of N". That
 * is the Blueprint's reading of a split ("how many of my people are where"),
 * and it puts the three counts in one column where a reader compares them,
 * instead of three tiles side by side. The rows are counts, never a rate: the
 * bars draw each band against the whole list, and no percentage is printed.
 *
 * **The band colour moved from the bar to the word.** `BySourceBars` draws its
 * marks in brand or an identity colour and refuses a status one by type,
 * because a bar in a list of sources is not a verdict. Warning and Critical
 * are verdicts, so they are said where a verdict belongs on this page: the
 * band `Badge` on every person in "Most messaged" still wears warn and bad.
 * The rejected alternative was a hand-made bar list that kept amber and red
 * bars — a second by-source component, which is the drift the kit exists to
 * stop.
 *
 * "Most messaged" is a `PersonList` of `PersonRow`s rather than a table: a
 * person is a name, a line of facts and a band, and the row reads as one. The
 * contact is shown exactly as it was in the table — this screen has always
 * shown the address of a person a workspace messages too often, so the person
 * can be found and rested — and `PersonRow` prints it as given; it never
 * formats or reveals more than the API sent.
 */
import { Link } from 'react-router-dom';

import {
  Badge,
  BySourceBars,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  MetricPanel,
  MetricSection,
  PageHeader,
  PersonList,
  PersonRow,
  type BadgeTone,
  type DataTableColumn,
} from '@wend/ui';
import {
  PRESSURE_BAND_COPY,
  PRESSURE_BAND_VALUES,
  PRESSURE_SEGMENT_LIMIT,
  PRESSURE_WARNING_AT,
  PRESSURE_WINDOW_DAYS,
  type PressureBand,
  type PressuredPerson,
  type SegmentPressureRow,
  type WorkspacePressure,
} from '@wend/shared';

import { AppLayout } from '../../components/AppLayout';
import { ErrorState } from '../../components/ErrorState';
import { fetchWorkspacePressure } from '../../lib/api/sense';
import { formatCount, formatDate, pluralise } from '../../lib/format';
import { PILLAR_IDENTITY } from '../../lib/pillars';
import { useAsyncData } from '../../lib/use-async-data';
import { MoreDetail } from '../templates/MoreDetail';

/** A band's colour, said once. Healthy is not "good news" — it is the absence of pressure — so it is neutral. */
const BAND_TONE: Readonly<Record<PressureBand, BadgeTone>> = {
  healthy: 'neutral',
  warning: 'warn',
  critical: 'bad',
};

export function PressurePage() {
  const pressure = useAsyncData((signal) => fetchWorkspacePressure(signal), []);
  const { data, error, status, reload } = pressure;

  return (
    <AppLayout title="Pressure">
      <PageHeader
        eyebrow={PILLAR_IDENTITY.sense.label}
        icon={PILLAR_IDENTITY.sense.icon}
        lede="Whether the same people are being messaged too often."
      />
      <div className="flex flex-col gap-6" data-role="pressure-page">
        {status === 'error' && data === null && (
          <ErrorState
            error={error}
            title="Could not load message pressure"
            onRetry={reload}
            className="flex flex-col items-start gap-3 rounded-base border border-line px-4 py-5"
          />
        )}

        {data !== null && <PressureContent pressure={data} />}
      </div>
    </AppLayout>
  );
}

function PressureContent({ pressure }: { pressure: WorkspacePressure }) {
  if (pressure.people === 0) {
    return (
      <p className="text-[0.92rem] text-muted" data-role="pressure-empty">
        Nobody is in Audiences.
      </p>
    );
  }

  return (
    <>
      <section className="flex flex-col gap-3" aria-labelledby="pressure-bands-title">
        <h2 id="pressure-bands-title" className="text-[0.95rem] font-medium text-text">
          Messages over the last {PRESSURE_WINDOW_DAYS} days
        </h2>

        <MetricPanel
          data-role="pressure-bands"
          kicker="People in Audiences"
          figureSize="lg"
          figure={{ kind: 'measured', value: pressure.people }}
          sub={<span data-role="pressure-touches">{pluralise(pressure.touches, 'message')} sent to them</span>}
        >
          <MetricSection kicker="By messages each">
            <BySourceBars
              rows={PRESSURE_BAND_VALUES.map((band) => ({
                key: band,
                label: (
                  <span data-band={band}>
                    {PRESSURE_BAND_COPY[band].label}{' '}
                    <span className="text-muted">· {PRESSURE_BAND_COPY[band].range}</span>
                  </span>
                ),
                count: pressure.bands[band],
                of: pressure.people,
              }))}
            />
          </MetricSection>
          {pressure.untouched > 0 && (
            <p className="mt-3 text-[0.76rem] text-muted" data-role="pressure-untouched">
              {pluralise(pressure.untouched, 'person', 'people')} in Healthy have had no messages at all.
            </p>
          )}
        </MetricPanel>

        <MoreDetail summary="What counts as a message">
          <p>
            One that Wend handed to Amazon or Meta, by email or WhatsApp, to one person — however many
            of your lists they are on. Ads are not counted.
          </p>
        </MoreDetail>
      </section>

      <SegmentsUnderPressure rows={pressure.segments} />
      <MostMessaged rows={pressure.mostTouched} />
    </>
  );
}

function SegmentsUnderPressure({ rows }: { rows: readonly SegmentPressureRow[] }) {
  const columns: DataTableColumn<SegmentPressureRow>[] = [
    {
      key: 'name',
      header: 'Segment',
      cell: (row) => (
        <Link
          to={`/segments/${row.segmentId}`}
          className="min-w-0 truncate font-medium text-text underline-offset-4 hover:underline"
        >
          {row.name}
        </Link>
      ),
    },
    { key: 'people', header: 'People', align: 'right', cell: (row) => formatCount(row.people) },
    { key: 'warning', header: 'Warning', align: 'right', cell: (row) => formatCount(row.warning) },
    { key: 'critical', header: 'Critical', align: 'right', cell: (row) => formatCount(row.critical) },
  ];

  return (
    <Card data-role="pressure-segments">
      <CardHeader className="p-[18px] pb-3.5">
        {/* "Top" because the list is capped and returns no total: without it a
            workspace with twelve such segments would read five as all of them. */}
        <CardTitle className="text-[0.95rem]">Top {PRESSURE_SEGMENT_LIMIT} segments under pressure</CardTitle>
      </CardHeader>
      <CardContent className="p-[18px] pt-0">
        <DataTable
          label="Segments with people at Warning or Critical"
          columns={columns}
          rows={rows}
          getRowKey={(row) => row.segmentId}
          empty={<p className="py-3 text-[0.88rem] text-muted">No segment has anyone at Warning or Critical.</p>}
        />
      </CardContent>
    </Card>
  );
}

function MostMessaged({ rows }: { rows: readonly PressuredPerson[] }) {
  return (
    <Card data-role="pressure-people">
      <CardHeader className="p-[18px] pb-3.5">
        <CardTitle className="text-[0.95rem]">Most messaged</CardTitle>
      </CardHeader>
      <CardContent className="p-[18px] pt-0">
        {rows.length === 0 ? (
          <p className="py-3 text-[0.88rem] text-muted">
            Nobody has had {PRESSURE_WARNING_AT} or more messages in the last {PRESSURE_WINDOW_DAYS} days.
          </p>
        ) : (
          <PersonList aria-label={`People with the most messages in the last ${PRESSURE_WINDOW_DAYS} days`}>
            {rows.map((row) => {
              const address = row.email ?? row.phone;
              return (
                <PersonRow
                  key={row.personId}
                  seed={row.personId}
                  name={row.name ?? address ?? 'No name'}
                  meta={`${pluralise(row.touches, 'message')} · last ${formatDate(row.lastTouchedAt)}`}
                  {...(row.name !== null && address !== null ? { contact: address } : {})}
                  tag={<Badge tone={BAND_TONE[row.band]}>{PRESSURE_BAND_COPY[row.band].label}</Badge>}
                />
              );
            })}
          </PersonList>
        )}
      </CardContent>
    </Card>
  );
}
